import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { ordersApi } from '@/features/orders';
import { useRestaurantStore } from '@/features/restaurants/store';
import { useNotificationsStore } from '@/features/notifications/store';
import { useIsDesktop } from '@/shared/hooks/useIsDesktop';
import { usePrinterStore } from '@/features/printer/store';
import { buildOrderPayload } from '@/features/printer/lib/buildOrderPayload';
import { printOrderWithSavedConfig } from '@/features/printer/lib/printOrder';

/** Skip orders whose ORDER_ACCEPTED notification is older than this. */
const NEW_ORDER_THRESHOLD_MS = 60_000;
/** Cap on persisted set size — we only need recent IDs to dedupe. */
const SEEN_CAP = 200;
const STORAGE_KEY = 'printer-seen-order-ids';

function loadSeen(): Set<string> {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.slice(-SEEN_CAP));
  } catch {
    return new Set();
  }
}

function saveSeen(set: Set<string>): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const arr = Array.from(set).slice(-SEEN_CAP);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  } catch {
    /* quota exceeded / disabled — skip silently */
  }
}

/**
 * Listens for ORDER_ACCEPTED events on the existing notifications stream and
 * fires a print job for each one — once per orderId per session.
 *
 * Triggers on ORDER_ACCEPTED (not ORDER_PLACED) so that:
 *  - Cash orders: print only after the owner explicitly accepts.
 *  - Online orders: print only after acceptance (payment capture), not on
 *    authorization (ORDER_PLACED). autoAcceptedBySystem has no effect on the
 *    print trigger — we print regardless of whether a human or the sweeper accepted.
 *
 * No-ops outside the Electron desktop app or when the user has switched off
 * the master `enabled` / `autoPrint` toggles. Failures surface as a toast.
 *
 * Dedupe semantics:
 *  - The session-scoped `seenOrderIds` Set rejects duplicate prints from
 *    backfill replays, SSE reconnects, or rapid re-mounts.
 *  - `isInitialFlush` is honored — the seed window after page load is
 *    skipped so we don't reprint everything that's been sitting in the
 *    inbox for hours.
 *  - `Order.isPrinted` from the backend is honored — if the order has
 *    already been marked printed (e.g. by the legacy printer device or a
 *    sibling tablet), we skip it.
 */
export function usePrintOnNewOrder(restaurantId: string | undefined): void {
  const isDesktop = useIsDesktop();
  const enabled = usePrinterStore((s) => s.enabled);
  const autoPrint = usePrinterStore((s) => s.autoPrint);
  const mode = usePrinterStore((s) => s.mode);
  const network = usePrinterStore((s) => s.network);
  const osPrinterName = usePrinterStore((s) => s.osPrinterName);
  const paperWidthMm = usePrinterStore((s) => s.paperWidthMm);
  const currentRestaurant = useRestaurantStore((s) => s.currentRestaurant);

  // Persist across re-renders for the lifetime of this hook mount, AND
  // across page reloads via localStorage. Without persistence, every Cmd-R
  // reset the set and re-printed every recent order. Capped to recent 200
  // IDs to keep the storage value small.
  const seenOrderIdsRef = useRef<Set<string>>(loadSeen());

  useEffect(() => {
    if (!restaurantId || !isDesktop || !enabled || !autoPrint) return;

    // Subscribe directly to the notifications store. We can't use the React
    // selector form because the events stream pushes fast and we want to
    // process every change, not just re-render once.
    const unsubscribe = useNotificationsStore.subscribe((state, prev) => {
      if (state.isInitialFlush) return;
      if (state.items === prev.items) return;

      const now = Date.now();
      // Find ORDER_ACCEPTED notifications that aren't yet in the seen set
      // AND were created recently. The age check protects against the
      // initial seedList (which dumps the unread inbox into items[]) — we
      // do NOT want to reprint orders that arrived hours ago.
      const fresh = state.items.filter((n) => {
        if (n.eventType !== 'ORDER_ACCEPTED') return false;
        if (seenOrderIdsRef.current.has(n.orderId)) return false;
        const createdMs = new Date(n.createdAt).getTime();
        if (Number.isNaN(createdMs)) return false;
        if (now - createdMs > NEW_ORDER_THRESHOLD_MS) return false;
        return true;
      });
      if (fresh.length === 0) return;

      for (const notification of fresh) {
        // Mark as seen *before* the async work so reconnect storms don't
        // queue the same orderId multiple times.
        seenOrderIdsRef.current.add(notification.orderId);
        saveSeen(seenOrderIdsRef.current);
        void firePrint(notification.orderId);
      }
    });

    const firePrint = async (orderId: string): Promise<void> => {
      try {
        const order = await ordersApi.getOrder(restaurantId, orderId);
        if (order.isPrinted) {
          // Backend says someone else already printed (legacy device, tablet,
          // etc.) — respect that.
          return;
        }
        const payload = buildOrderPayload(order, currentRestaurant);
        const result = await printOrderWithSavedConfig(payload, {
          mode,
          network,
          osPrinterName,
          paperWidthMm,
        });
        if (!result.ok) {
          toast.error(`Auto-print failed for #${order.orderNumber ?? orderId}`, {
            description: result.error ?? 'Unknown error',
          });
        }
      } catch (err) {
        toast.error(`Auto-print failed`, {
          description: (err as Error).message,
        });
      }
    };

    return () => {
      unsubscribe();
    };
  }, [
    restaurantId,
    isDesktop,
    enabled,
    autoPrint,
    mode,
    network,
    osPrinterName,
    paperWidthMm,
    currentRestaurant,
  ]);
}
